import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Star, Award, ArrowLeft, Crown } from 'lucide-react';
import { publicAPI } from '../lib/api';
import api from '../lib/api';

export default function ResultsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [programName, setProgramName] = useState('Futurum Innovation Awards');
  const [programSeason, setProgramSeason] = useState('Season II');

  useEffect(() => {
    publicAPI.getConfig()
      .then(r => {
        if (r.data.program_name) setProgramName(r.data.program_name);
        if (r.data.program_season) setProgramSeason(r.data.program_season);
      })
      .catch(() => {});

    api.get('/public/results')
      .then(r => setData(r.data))
      .catch(() => setData({ published: false, projects: [] }))
      .finally(() => setLoading(false));
  }, []);

  const winners   = data?.projects?.filter(p => p.is_winner)   || [];
  const finalists = data?.projects?.filter(p => p.is_finalist && !p.is_winner) || [];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--ink)',
      backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(201,162,39,0.06) 0%, transparent 60%)',
    }}>
      {/* Header */}
      <div style={{
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        padding: '20px 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600, color: 'var(--gold)', letterSpacing: '0.08em' }}>
          {programName.toUpperCase()}
        </div>
        <Link to="/" style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
          <ArrowLeft size={14} /> Back to home
        </Link>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '60px 24px' }}>
        {/* Page title */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', letterSpacing: '0.2em', color: 'var(--teal)', textTransform: 'uppercase', marginBottom: 12 }}>
            {programSeason}
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 300, lineHeight: 1.1, marginBottom: 16 }}>
            Final <span style={{ color: 'var(--gold)' }}>Results</span>
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', maxWidth: 500, margin: '0 auto' }}>
            The outstanding projects selected by our panel of independent judges at the {programSeason} Finale.
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>
            <div style={{ width: 32, height: 32, border: '2px solid rgba(255,255,255,0.1)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
            Loading results…
          </div>
        ) : !data?.published ? (
          <div style={{ textAlign: 'center', padding: '80px 40px' }}>
            <Trophy size={56} style={{ color: 'var(--gold)', opacity: 0.3, margin: '0 auto 20px', display: 'block' }} />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 400, marginBottom: 12 }}>
              Results not yet published
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              Check back after the finale on <strong style={{ color: 'var(--text-secondary)' }}>30 July 2026</strong>.
            </p>
          </div>
        ) : (
          <>
            {/* Winners */}
            {winners.length > 0 && (
              <div style={{ marginBottom: 56 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                  <Trophy size={20} color="var(--gold)" />
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 500, color: 'var(--gold)' }}>
                    Winners
                  </h2>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {winners.map((p, i) => (
                    <ProjectCard key={p.id} project={p} rank={i + 1} isWinner />
                  ))}
                </div>
              </div>
            )}

            {/* Finalists */}
            {finalists.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                  <Star size={18} color="var(--teal)" />
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 500, color: 'var(--teal)' }}>
                    Finalists
                  </h2>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {finalists.map(p => (
                    <ProjectCard key={p.id} project={p} isWinner={false} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 80, paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>
            {programName} &bull; Obafemi Awolowo University, Ile-Ife<br />
            Powered by Pervium Consulting &bull;{' '}
            <a href="https://futurumawards.com" style={{ color: 'var(--teal)', textDecoration: 'none' }}>futurumawards.com</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ project, rank, isWinner }) {
  const isTeam = project.members && project.members.length > 1;

  return (
    <div style={{
      padding: '24px 28px',
      borderRadius: 14,
      background: isWinner ? 'rgba(201,162,39,0.05)' : 'rgba(255,255,255,0.02)',
      border: `1px solid ${isWinner ? 'rgba(201,162,39,0.3)' : 'rgba(45,212,191,0.15)'}`,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Rank badge */}
      {rank && (
        <div style={{
          position: 'absolute', top: 20, right: 24,
          fontFamily: 'var(--font-mono)', fontSize: '2rem', fontWeight: 700,
          color: isWinner ? 'rgba(201,162,39,0.15)' : 'rgba(255,255,255,0.05)',
          lineHeight: 1,
        }}>
          #{rank}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        {/* Medal */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: isWinner ? 'rgba(201,162,39,0.15)' : 'rgba(45,212,191,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isWinner
            ? <Trophy size={18} color="var(--gold)" />
            : <Star size={16} color="var(--teal)" />
          }
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 4 }}>
            {project.topic_title}
          </div>

          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 10 }}>
            <span style={{ color: 'var(--teal)' }}>{project.domain}</span>
            {project.lecturer_name && <span> &bull; Supervised by {project.lecturer_name}</span>}
            {project.final_score && (
              <span style={{ color: 'var(--gold)', fontFamily: 'var(--font-mono)', marginLeft: 8 }}>
                Score: {parseFloat(project.final_score).toFixed(2)}/10
              </span>
            )}
          </div>

          {/* Team or solo */}
          {isTeam ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {project.members.map(m => (
                <div key={m.student_id} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  fontSize: '0.8rem', color: 'var(--text-secondary)',
                }}>
                  {m.is_lead && <Crown size={12} color="var(--gold)" />}
                  {m.full_name}
                  {m.is_lead && <span style={{ fontSize: '0.7rem', color: 'var(--gold)' }}>(Lead)</span>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              {project.student_name}
              {project.department && <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>&bull; {project.department}</span>}
            </div>
          )}

          {project.abstract && (
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.65, maxWidth: 560 }}>
              {project.abstract}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
